import Commons from '../../../../Services/Common/Common';
import dayjs from 'dayjs';
import Store from '../../../../Store/Store';

/**
 * HEA_BASIC_01 스키마
 * - fields: 폼/그리드에서 공통으로 쓰는 필드 정의
 *   - initial: 초기값
 *   - label: 필드명
 *   - required: 필수 입력 여부
 *   - requiredWhen: 언제 필수인지 조건
 *
 * - mapper: 저장/삭제 API payload 생성 규칙
 *   - mapRowsBySchema(rows, mapper, context)로 payload를 만듬
 *   - context에는 userInfo 등이 들어오며(mapRowsBySchema에 기본족으로 정의되어 있음),
 *     화면에서 추가로 필요한 값(custCd 등)을 context로 넘겨줄 수 있다.
 */
export const HEA_BASIC_01_Schema = {
  fields: {
    no: { initial: '', label: 'NO' },
    hcCd: { initial: '', label: '건강진단코드' },
    hcId: { initial: '', label: '사용자ID' },
    custCd: { initial: '', label: '사업장코드' },
    depNm1: { initial: '', label: '본부' },
    depNm2: { initial: '', label: '부서' },
    depNm3: { initial: '', label: '팀' },
    depNm4: { initial: '', label: '파트' },
    position: { initial: '', label: '직책' },
    name: { initial: '', label: '성명', required: 'Y' },
    hcCycle: { initial: '', label: '검진주기(년)', required: 'Y' },
    hcDate: { initial: '', label: '검진일', required: 'Y' },
    hcEffeDate: { initial: '', label: '유효일자', required: 'Y' },
    hcAlarmYn: { initial: '', label: '알람여부(Y/N)', required: 'Y' },
    hcCheckupCenter: { initial: '', label: '검진기관', required: 'Y' },
    hcCheckupAttachmentFileName: { initial: '', label: '검진표 첨부', required: 'Y' },
    hcPostcareAttachmentFileName: { initial: '', label: '사후관리조치 첨부', required: 'Y' },
    hcRemark: { initial: '', label: '비고' },
    hcSubstance: { initial: '', label: '취급물질' },
    hcNoise: { initial: '', label: '소음작업' },
    hcNight: { initial: '', label: '야간작업' },
    hcGubun: { initial: '', label: '건강검진(특수,일반)' },
    alarmId: { initial: '', label: '알람ID' },
  },

  mapper: {
    search1: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || '') || '',
      HcYear: (_, context) =>
        Commons.EncryptAES(context.search?.year || dayjs().format('YYYY')) ||
        '',
    },
    search2: {
      UserId: (_, context) => context.userInfo?.userid || '',
    },
    delete1: {
      HcCd: (row) => Commons.EncryptAES(row.hcCd || '') || '',
      AlarmId: (row) =>
        Commons.EncryptAES(row.alarmId ? row.alarmId.toString() : '0') || '',
    },
    save1: {
      HcCd: (row) => {
        const hcCd = row.hcCd;
        const hcCdValue =
          hcCd && hcCd.toString().trim() !== '' ? hcCd.toString() : '0';
        return Commons.EncryptAES(hcCdValue) || '';
      },
      HcId: (row) => Commons.EncryptAES(row.hcId || '') || '',
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      HcCycle: (row) => Commons.EncryptAES(row.hcCycle || '') || '',
      HcDate: (row) => {
        if (!row.hcDate || !dayjs(row.hcDate).isValid()) return '';
        return Commons.EncryptAES(dayjs(row.hcDate).format('YYYY-MM-DD')) || '';
      },
      HcYear: (row, context) => {
        const year =
          row.hcDate && dayjs(row.hcDate).isValid()
            ? dayjs(row.hcDate).format('YYYY')
            : context.search?.year || '';
        return Commons.EncryptAES(year) || '';
      },
      HcEffeDate: (row) => {
        if (!row.hcEffeDate || !dayjs(row.hcEffeDate).isValid()) return '';
        return Commons.EncryptAES(dayjs(row.hcEffeDate).format('YYYY-MM-DD')) || '';
      },
      HcAlarmYn: (row) => Commons.EncryptAES(row.hcAlarmYn || '') || '',
      HcCheckupCenter: (row) =>
        Commons.EncryptAES(row.hcCheckupCenter || '') || '',
      HcCheckupAttachmentFileName: (row) =>
        Commons.EncryptAES(row.hcCheckupAttachmentFileName) || '',
      HcCheckupAttachmentSaveName: (row) =>
        Commons.EncryptAES(row.hcCheckupAttachmentSaveName) || '',
      HcCheckupAttachmentFilePath: (row, context) => {
        // 새 파일 업로드 시: previewFilePath에서 saveName 추출하여 최종 경로 생성
        const previewPath = row.hcCheckupPreviewFilePath || '';
        if (previewPath) {
          const saveName = previewPath.split('/').pop() || '';
          const finalPath = `HealthCheckup/${context.search?.custCd}/HEA_BASIC_01/Checkup/${saveName}`;
          return Commons.EncryptAES(finalPath) || '';
        }

        // 기존 파일 유지 시: hcCheckupAttachmentFilePath 직접 사용
        const existingFilePath = row.hcCheckupAttachmentFilePath || '';
        if (!existingFilePath) return '';
        return Commons.EncryptAES(existingFilePath) || '';
      },
      HcPostcareAttachmentFileName: (row) =>
        Commons.EncryptAES(row.hcPostcareAttachmentFileName) || '',
      HcPostcareAttachmentSaveName: (row) =>
        Commons.EncryptAES(row.hcPostcareAttachmentSaveName) || '',
      HcPostcareAttachmentFilePath: (row, context) => {
        // 새 파일 업로드 시: previewFilePath에서 saveName 추출하여 최종 경로 생성
        const previewPath = row.hcPostcarePreviewFilePath || '';
        if (previewPath) {
          const saveName = previewPath.split('/').pop() || '';
          const finalPath = `HealthCheckup/${context.search?.custCd}/HEA_BASIC_01/PostCare/${saveName}`;
          return Commons.EncryptAES(finalPath) || '';
        }

        // 기존 파일 유지 시: hcPostcareAttachmentFilePath 직접 사용
        const existingFilePath = row.hcPostcareAttachmentFilePath || '';
        if (!existingFilePath) return '';
        return Commons.EncryptAES(existingFilePath) || '';
      },
      HcRemark: (row) => Commons.EncryptAES(row.hcRemark || '') || '',
      HcSubstance: (row) => Commons.EncryptAES(row.hcSubstance || '') || '',
      HcNoise: (row) => Commons.EncryptAES(row.hcNoise || '') || '',
      HcNight: (row) => Commons.EncryptAES(row.hcNight || '') || '',
      HcGubun: (row) => Commons.EncryptAES(row.hcGubun || '') || '',
      AlarmId: (row) =>
        Commons.EncryptAES(row.alarmId ? row.alarmId.toString() : '0') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
      CoCd: (_, context) =>
        Commons.EncryptAES(context.search?.coCd || '') || '',
      EmployeeNo: (row) => Commons.EncryptAES(row.employeeNo || '') || '',
      ProperName: () => {
        const companyInfo = Store.getState()?.BaseInfo?.companyInfo[0];
        return Commons.EncryptAES(companyInfo?.properName || '') || '';
      },
      DepNm1: (row) => Commons.EncryptAES(row.depNm1 || '') || '',
      DepNm2: (row) => Commons.EncryptAES(row.depNm2 || '') || '',
      DepNm3: (row) => Commons.EncryptAES(row.depNm3 || '') || '',
      DepNm4: (row) => Commons.EncryptAES(row.depNm4 || '') || '',
      Position: (row) => Commons.EncryptAES(row.position || '') || '',
      Name: (row) => Commons.EncryptAES(row.name || '') || '',
    },
    save2: {
      HcCd: (row) => Commons.EncryptAES(row.hcCd || '') || '',
      HcEffeDate: (row) => {
        if (!row.hcEffeDate || !dayjs(row.hcEffeDate).isValid()) return '';
        return Commons.EncryptAES(dayjs(row.hcEffeDate).format('YYYY-MM-DD')) || '';
      },
      HcAlarmYn: (row) => Commons.EncryptAES(row.hcAlarmYn || '') || '',
      CalId: (row) => Commons.EncryptAES(row.calId ? row.calId.toString() : '') || '',
      AlarmId: (row) =>
        Commons.EncryptAES(row.alarmId ? row.alarmId.toString() : '0') || '',
      HealthName: (row) => Commons.EncryptAES(row.name || '') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },
  },
};
